'use client'

import { cn } from '@brew/ui/cn'
import { lazy, Suspense, useCallback, useRef, useState } from 'react'
import { DeferredPanelBoundary } from '@/components/layout/deferred-panel-boundary'
import { APP_VISUAL_LAYOUT, type AppVisualKind } from './app-visual-layout'
import { AppVisualScaleProvider } from './app-visual-scale'
import { AppVisualStage } from './app-visual-stage'
import type { HomeVisualsData } from './visual-data'

/** The providers + scene chunk, fetched only when a visual nears the view. */
const SceneRoot = lazy(() => import('./scene-root'))

/**
 * Start loading a scene this far before it scrolls into view: well ahead on
 * a wide screen, where the scene is a working product, and a full screen
 * ahead on a phone, so the scene is on the page before the visitor reaches
 * it. (100px was too late there: with the idle wait and the chunk fetch, a
 * visual appeared about a second after it scrolled into view.) A visual
 * below the first view still waits for the idle moment below, so the app
 * code never runs inside the page's own hydration.
 */
const PRELOAD_MARGIN = { wide: '800px 0px', narrow: '900px 0px' } as const
const WIDE_SCREEN = '(min-width: 768px)'

/**
 * Once near, a scene still waits for an idle moment (at most this long), so
 * a visual just below the first view never loads its app code inside the
 * page's own hydration: on a phone that work was the page's longest task.
 */
const IDLE_TIMEOUT_MS = 600

/** Run `start` when the browser is idle (or soon, where it cannot say). */
function whenIdle(start: () => void): () => void {
  if (typeof window.requestIdleCallback === 'function') {
    const handle = window.requestIdleCallback(start, {
      timeout: IDLE_TIMEOUT_MS,
    })
    return () => window.cancelIdleCallback(handle)
  }
  const timer = window.setTimeout(start, 200)
  return () => window.clearTimeout(timer)
}

/**
 * Below this scale the stage is too small to work with (a phone column
 * shows it at about a third): an interactive scene turns into a picture of
 * itself there, `inert`, until the column grows again.
 */
const MIN_INTERACTIVE_SCALE = 0.6

/**
 * One of the home page's app visuals: REAL app components (see
 * `scene-root.tsx`) composed on a live sky. The stage is laid out at true
 * product scale (1024px wide) and scaled to the column, so every width shows
 * the same layout. The sky prerenders with the page; the scene and its app
 * code load only as the visual nears the viewport and the browser is idle,
 * then fade in.
 *
 * Each visual is one labelled `<figure>`. An interactive one
 * (`APP_VISUAL_LAYOUT[kind].isInteractive`) is a working copy of the
 * product the visitor can click, type into and drag; it runs entirely in the
 * browser (no session, no writes; see each scene). Below
 * `MIN_INTERACTIVE_SCALE` it turns `inert`, a picture of itself under the
 * figure's label; a static visual's scene is always `inert` and hidden from
 * assistive tech. A scene that fails to load leaves its visual on the bare
 * sky, never the page. Before the scale is measured (no JS yet), the frame clips the stage
 * to the column, so it never widens the page.
 */
export function AppVisual({
  kind,
  data,
  className,
}: {
  kind: AppVisualKind
  data: HomeVisualsData
  className?: string
}) {
  const layout = APP_VISUAL_LAYOUT[kind]
  const [isNear, setIsNear] = useState(false)
  const scaleRef = useRef(1)
  const sceneRef = useRef<HTMLDivElement | null>(null)
  const readScale = useCallback(() => scaleRef.current, [])

  // Interactivity follows the scale without a render: the attribute is the
  // only thing that changes.
  const applyInteractivity = useCallback(() => {
    const scene = sceneRef.current
    if (scene && layout.isInteractive) {
      scene.toggleAttribute('inert', scaleRef.current < MIN_INTERACTIVE_SCALE)
    }
  }, [layout.isInteractive])

  const attachScene = useCallback(
    (node: HTMLDivElement | null) => {
      sceneRef.current = node
      applyInteractivity()
    },
    [applyInteractivity]
  )

  const observe = useCallback(
    (node: HTMLElement | null) => {
      if (!node) {
        return
      }
      // Scale lives in a CSS variable, so resizing never re-renders React.
      const fit = () => {
        const scale = node.clientWidth / layout.width
        scaleRef.current = scale
        node.style.setProperty('--app-visual-scale', String(scale))
        applyInteractivity()
      }
      fit()
      const resize = new ResizeObserver(fit)
      resize.observe(node)
      let cancelIdle: (() => void) | null = null
      const near = new IntersectionObserver(
        ([entry]) => {
          if (entry?.isIntersecting) {
            near.disconnect()
            cancelIdle = whenIdle(() => setIsNear(true))
          }
        },
        {
          rootMargin: window.matchMedia?.(WIDE_SCREEN).matches
            ? PRELOAD_MARGIN.wide
            : PRELOAD_MARGIN.narrow,
        }
      )
      near.observe(node)
      return () => {
        resize.disconnect()
        near.disconnect()
        cancelIdle?.()
      }
    },
    [layout.width, applyInteractivity]
  )

  return (
    <figure
      ref={observe}
      aria-label={layout.label}
      className={cn('relative w-full overflow-hidden', className)}
      style={{ aspectRatio: `${layout.width} / ${layout.height}` }}
    >
      <div
        className="absolute top-0 left-0 origin-top-left"
        style={{
          width: layout.width,
          height: layout.height,
          transform: 'scale(var(--app-visual-scale, 1))',
        }}
      >
        <AppVisualStage
          width={layout.width}
          height={layout.height}
          chrome={layout.chrome}
          sky={layout.sky}
          innerClassName={layout.innerClassName}
        >
          {isNear ? (
            <div
              ref={attachScene}
              inert={layout.isInteractive ? undefined : true}
              aria-hidden={layout.isInteractive ? undefined : true}
              data-app-visual-scene={kind}
              className="absolute inset-0 animate-mk-scene-in motion-reduce:animate-none"
            >
              <AppVisualScaleProvider value={readScale}>
                <DeferredPanelBoundary
                  fallback={null}
                  panelName={`home_visual_${kind}`}
                >
                  <Suspense fallback={null}>
                    <SceneRoot kind={kind} data={data} />
                  </Suspense>
                </DeferredPanelBoundary>
              </AppVisualScaleProvider>
            </div>
          ) : null}
        </AppVisualStage>
      </div>
    </figure>
  )
}
